'use client';

import * as React from 'react';
import { ChatCloseIcon, useRoomContext } from '@livekit/components-react';
import type { TextStreamReader } from 'livekit-client';
import toast from 'react-hot-toast';
import {
  ASSISTANT_TOPIC,
  EMPTY_ASSISTANT_STATE,
  applyAssistantMessage,
  groupHints,
  isHintResolved,
  newHints,
  orderItems,
  parseAssistantMessage,
  timingView,
  runStatusLabel,
  statusLabel,
  visibleHints,
  type AssistantHint,
  type AgendaItem,
  type AssistantPanelState,
  type ItemTiming,
} from '@/lib/assistant-state';
import { ru } from '@/lib/ru';
import { startMeetingAssistant, type MeetingChatState } from '@/lib/assistant-chat';
import { AgimaBotIcon, BotComposer, BotFeed, CHECKLIST_CHIP, useBotChat } from './AssistantBot';

export { AgimaBotIcon };

function HintLine(props: { hint: AssistantHint; items: AgendaItem[] }) {
  const { hint, items } = props;
  const resolved = isHintResolved(hint, items);
  return (
    <li
      className="agima-assistant-hint"
      data-source={hint.source}
      data-resolved={resolved ? 'true' : undefined}
    >
      <span className="agima-assistant-hint-time">{formatTime(hint.at)}</span>
      <span className="agima-assistant-hint-text" data-kind={hint.source === 'timing' ? 'timing' : hint.kind}>
        {hint.source === 'timing'
          ? `${ru.assistantTimingHint}: `
          : hint.kind === 'question'
            ? `${ru.assistantQuestionHint}: `
            : hint.kind === 'speaker'
              ? `${ru.assistantSpeakerHint}: `
              : ''}
        {hint.text}
      </span>
      {resolved && <span className="agima-assistant-hint-done">{ru.assistantHintResolved}</span>}
    </li>
  );
}

/** Hints of one item or the general ones: the newest, the rest behind «ещё N». */
function HintList(props: { hints: AssistantHint[]; items: AgendaItem[] }) {
  const { hints, items } = props;
  const [expanded, setExpanded] = React.useState(false);
  const { shown, hidden } = visibleHints(hints, expanded);
  if (!hints.length) return null;
  return (
    <ul className="agima-assistant-hints">
      {shown.map((hint) => (
        <HintLine key={hint.id} hint={hint} items={items} />
      ))}
      {hidden > 0 && (
        <li className="agima-assistant-more">
          <button type="button" className="agima-assistant-link" onClick={() => setExpanded(true)}>
            {ru.assistantMore} {hidden}
          </button>
        </li>
      )}
    </ul>
  );
}

/** The model sometimes quotes the evidence itself: «…» around «…» reads badly. */
function unquote(text: string): string {
  return text.trim().replace(/^[«"„“]+/, '').replace(/[»"“”]+$/, '');
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function minutes(value: number): string {
  return value < 10
    ? (Math.round(value * 10) / 10).toString().replace('.', ',')
    : String(Math.round(value));
}

/** Spent against planned minutes of one item: a bar and «3/5 мин». */
function ItemTime(props: { timing: ItemTiming; open: boolean }) {
  const { timing, open } = props;
  if (!timing.plannedMin) return null;
  const ratio = timing.spentMin / timing.plannedMin;
  const state = ratio > 1.25 ? 'overtime' : ratio > 1 ? 'behind' : 'ok';
  return (
    <span
      className="agima-assistant-item-time"
      data-state={state}
      data-open={open ? 'true' : undefined}
    >
      <span className="agima-assistant-item-time-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </span>
      <span>
        {minutes(timing.spentMin)}/{timing.plannedMin} {ru.assistantMinutes}
      </span>
    </span>
  );
}

/** A collapsible block of the stream: title, count and a «Показать/Скрыть» pseudo-link. */
function StreamBlock(props: {
  title: string;
  count?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  showLabel?: string;
  hideLabel?: string;
  children: React.ReactNode;
}) {
  const { title, count, open, onToggle } = props;
  return (
    <section className="agima-stream-block" data-open={open ? 'true' : undefined}>
      <button type="button" className="agima-stream-block-head" onClick={onToggle} aria-expanded={open}>
        <span className="agima-stream-block-chevron" aria-hidden="true" />
        <span className="agima-stream-block-title">{title}</span>
        {count !== undefined && count !== null && (
          <span className="agima-stream-block-count">{count}</span>
        )}
        <span className="agima-pseudo-link">
          {open ? (props.hideLabel ?? ru.assistantHide) : (props.showLabel ?? ru.assistantShow)}
        </span>
      </button>
      {open && <div className="agima-stream-block-body">{props.children}</div>}
    </section>
  );
}

const EMPTY_AGENDA_CHIPS = [CHECKLIST_CHIP, 'identify_meeting', 'lead_agenda'];

/**
 * AGIMA Bot inside the call, one stream: the assistant's agenda, hints and
 * transcript fold into blocks on top, the bot conversation follows, the input
 * box sits at the bottom. The agenda comes from the `octor.assistant` text
 * stream, which the assistant sends to AGIMA identities only; guests never
 * mount this component at all (see AgimaVideoConference). The bot needs a
 * meetingId.
 */
export function AssistantPanel(props: {
  open: boolean;
  onClose: () => void;
  meetingId?: string | null;
  assistantEnabled?: boolean;
}) {
  const { open, onClose, meetingId } = props;
  const room = useRoomContext();
  const [state, setState] = React.useState<AssistantPanelState>(EMPTY_ASSISTANT_STATE);
  const [assistantOn, setAssistantOn] = React.useState(Boolean(props.assistantEnabled));
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);
  const startAssistant = async () => {
    if (!meetingId || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      await startMeetingAssistant(meetingId);
      setAssistantOn(true);
    } catch (error) {
      setStartError((error as Error).message);
    } finally {
      setStarting(false);
    }
  };
  // The bot wrote a checklist (prepare turns the assistant on).
  const onBotReplied = React.useCallback((chat: MeetingChatState) => {
    if (chat.assistantEnabled) setAssistantOn(true);
  }, []);
  const chat = useBotChat(meetingId, onBotReplied);
  const listening = state.connected && state.runStatus === 'listening';
  const reloadChat = chat.reload;
  // The chips follow the call: «Дай выжимку» and «Сделай репорт» appear once
  // the assistant listens. Octor learns it from the assistant's debounced
  // state write, so ask a bit later and once more.
  React.useEffect(() => {
    if (!listening) return;
    const timers = [5000, 20000].map((ms) => window.setTimeout(reloadChat, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening]);

  const [agendaOpen, setAgendaOpen] = React.useState(true);
  const [hintsOpen, setHintsOpen] = React.useState(true);
  const [transcriptOpen, setTranscriptOpen] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(() => new Set());
  const stateRef = React.useRef(state);
  const openRef = React.useRef(open);
  stateRef.current = state;
  openRef.current = open;

  React.useEffect(() => {
    let disposed = false;
    const handler = async (reader: TextStreamReader) => {
      const text = await reader.readAll();
      if (disposed) return;
      const message = parseAssistantMessage(text);
      if (!message) return;
      const previous = stateRef.current;
      const next = applyAssistantMessage(previous, message);
      if (next === previous) return;
      stateRef.current = next;
      setState(next);
      if (openRef.current) return;
      // Panel closed: surface what the host would otherwise miss.
      for (const hint of newHints(previous.hints, next.hints)) {
        toast(hint.text, {
          id: `assistant-${hint.id}`,
          icon: hint.source === 'timing' ? '⏱' : hint.kind === 'question' ? '❓' : '💡',
          duration: 7000,
        });
      }
      if (message.type === 'status' && (message.level === 'warn' || message.level === 'error')) {
        toast.error(String(message.text), {
          id: `assistant-note-${next.notes[0]?.id}`,
          duration: 8000,
        });
      }
    };
    try {
      room.registerTextStreamHandler(ASSISTANT_TOPIC, handler);
    } catch (error) {
      console.warn('assistant text stream handler not registered', error);
    }
    return () => {
      disposed = true;
      try {
        room.unregisterTextStreamHandler(ASSISTANT_TOPIC);
      } catch {
        // already gone
      }
    };
  }, [room]);

  // The clock ticks between snapshots: re-render every 15 s while the panel lives.
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);
  const timing = timingView(state, nowMs);

  // New messages of the conversation scroll the stream down; the first load
  // leaves the agenda on top.
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const messageCount = chat.state?.messages.length ?? 0;
  const seenMessages = React.useRef<number | null>(null);
  // Others' messages arrive by polling: follow them only when the reader is
  // already at the bottom, never pull them away from the agenda.
  const nearBottom = React.useRef(true);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  React.useEffect(() => {
    if (!chat.state) return;
    const first = seenMessages.current === null;
    seenMessages.current = messageCount;
    if (first && !chat.pending) return;
    const el = scrollRef.current;
    if (el && (chat.pending || nearBottom.current)) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageCount, chat.pending, chat.failed]);

  const items = orderItems(state.items);
  const closed = state.summary.done + state.summary.skipped;
  const hints = groupHints(state.hints, state.items);
  const running = state.connected || assistantOn;
  // Enabled but not in the room (the call restarted, the assistant fell):
  // after a grace period the host can start it by hand.
  const [mountedAt] = React.useState(() => Date.now());
  const stuck = assistantOn && !state.connected && nowMs - mountedAt > 20000;
  const botReady = Boolean(chat.state?.enabled && chat.state.available) && !chat.pending;
  const emptyChips = (chat.state?.chips ?? []).filter((chip) => EMPTY_AGENDA_CHIPS.includes(chip.id));
  const toggleItem = (id: string) =>
    setExpandedItems((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const emptyAgenda = (
    <div className="agima-stream-empty">
      <div className="agima-stream-empty-line">
        <span>{running && state.connected ? ru.assistantNoAgendaShort : ru.agendaEmptyShort}</span>
        {meetingId && (
          <span className="agima-help" tabIndex={0} role="button" aria-label={ru.agendaHelp}>
            ?
            <span className="agima-help-tip" role="tooltip">
              <span>{ru.agendaEmpty}</span>
              <ol>
                <li>{ru.agendaEmptyStep1}</li>
                <li>{ru.agendaEmptyStep2}</li>
                <li>{running ? ru.agendaEmptyStep3Running : ru.agendaEmptyStep3}</li>
              </ol>
            </span>
          </span>
        )}
      </div>
      {meetingId && (
        <>
          {emptyChips.length > 0 && (
            <div className="agima-bot-chips">
              {emptyChips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className="agima-bot-chip"
                  disabled={!botReady}
                  onClick={() => chat.pickChip(chip.id, chip.label)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div
      className="lk-chat agima-assistant"
      style={{ display: open ? 'grid' : 'none' }}
      aria-live="polite"
    >
      <header className="agima-stream-head">
        <div className="agima-stream-title">
          <AgimaBotIcon size={22} />
          <span>{ru.assistant}</span>
          <button
            type="button"
            className="lk-button lk-close-button agima-assistant-close"
            onClick={onClose}
            aria-label={ru.close}
          >
            <ChatCloseIcon />
          </button>
        </div>
        <div
          className="agima-assistant-status"
          data-run-status={state.connected ? state.runStatus : assistantOn ? 'connecting' : 'off'}
        >
          <span className="agima-assistant-status-dot" />
          <span>{running ? runStatusLabel(state.runStatus, state.connected) : ru.assistantNotRunning}</span>
          {state.kind === 'rehearsal' && <span className="agima-assistant-kind">{ru.assistantRehearsal}</span>}
          {state.summary.total > 0 && (
            <span className="agima-assistant-progress">
              {closed} из {state.summary.total}
            </span>
          )}
          {(!running || stuck) && meetingId && (
            <button
              type="button"
              className="agima-assistant-start"
              disabled={starting}
              onClick={() => void startAssistant()}
            >
              {starting ? ru.assistantStarting : ru.assistantStart}
            </button>
          )}
        </div>
        {startError && <p className="agima-bot-error-text">{startError}</p>}
      </header>

      <div className="agima-stream-scroll" ref={scrollRef}>
        {state.notes.map((note) => (
          <div key={note.id} className="agima-assistant-note" data-level={note.level}>
            {note.text}
          </div>
        ))}

        <StreamBlock
          title={ru.assistantAgenda}
          count={state.summary.total > 0 ? `${closed}/${state.summary.total}` : undefined}
          open={agendaOpen}
          onToggle={() => setAgendaOpen((v) => !v)}
        >
          {items.length === 0 ? (
            running && !state.connected ? (
              <p className="agima-assistant-muted">{ru.assistantWaiting}</p>
            ) : (
              emptyAgenda
            )
          ) : (
            <>
              {timing && (
                <div
                  className="agima-assistant-timing"
                  data-state={timing.overtime ? 'overtime' : timing.shiftMin >= 2 ? 'behind' : 'ok'}
                >
                  {timing.started ? (
                    <>
                      <span>
                        {Math.floor(timing.elapsedMin)} из {timing.plannedMin} {ru.assistantMinutes}
                      </span>
                      {Math.abs(timing.shiftMin) >= 1 && (
                        <span className="agima-assistant-timing-behind">
                          {timing.shiftMin > 0 ? ru.assistantBehindPlan : ru.assistantAheadPlan}{' '}
                          {Math.round(Math.abs(timing.shiftMin))} {ru.assistantMinutes}
                        </span>
                      )}
                    </>
                  ) : (
                    <span>
                      {ru.assistantClockWaiting} · {timing.plannedMin} {ru.assistantMinutes}
                    </span>
                  )}
                  <div className="agima-assistant-timing-bar" aria-hidden="true">
                    <div
                      style={{
                        width: `${Math.min(100, (timing.elapsedMin / timing.plannedMin) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              <ul className="agima-assistant-items">
                {items.map((item) => {
                  const current = !!timing?.started && timing.currentItemId === item.id;
                  const isClosed = item.status === 'done' || item.status === 'skipped';
                  const expanded = expandedItems.has(item.id);
                  // Closed items fold to one line; the current one shows its rule.
                  const details = !isClosed || expanded;
                  const itemHints = hints.byItem.get(item.id) ?? [];
                  const itemTime = timing?.items.get(item.id);
                  return (
                    <li
                      key={item.id}
                      className="agima-assistant-item"
                      data-status={item.status}
                      data-kind={item.kind}
                      data-current={current ? 'true' : undefined}
                      data-collapsed={details ? undefined : 'true'}
                    >
                      <span className="agima-assistant-item-mark" aria-hidden="true" />
                      <div className="agima-assistant-item-body">
                        <div
                          className="agima-assistant-item-title"
                          onClick={isClosed ? () => toggleItem(item.id) : undefined}
                          role={isClosed ? 'button' : undefined}
                        >
                          {item.title}
                        </div>
                        <div className="agima-assistant-item-meta">
                          <span>{statusLabel(item.status)}</span>
                          {itemTime ? (
                            <ItemTime timing={itemTime} open={!isClosed} />
                          ) : item.minutes ? (
                            <span>
                              {item.minutes} {ru.assistantMinutes}
                            </span>
                          ) : null}
                          {item.speaker && (
                            <span className="agima-assistant-item-speaker">
                              {ru.assistantSpeaker}: {item.speaker}
                            </span>
                          )}
                          {item.priority && (
                            <span className="agima-assistant-tag">{ru.assistantPriority}</span>
                          )}
                          {current && (
                            <span className="agima-assistant-tag" data-kind="now">
                              {ru.assistantNow}
                            </span>
                          )}
                          {isClosed && itemHints.length > 0 && !expanded && (
                            <span className="agima-assistant-muted">
                              {ru.assistantHintsCount} {itemHints.length}
                            </span>
                          )}
                        </div>
                        {current && item.doneWhen && (
                          <p className="agima-assistant-done-when">
                            {ru.assistantDoneWhen}: {item.doneWhen}
                          </p>
                        )}
                        {details && item.evidence && (
                          <p className="agima-assistant-evidence">«{unquote(item.evidence)}»</p>
                        )}
                        {details && <HintList hints={itemHints} items={state.items} />}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </StreamBlock>

        {hints.general.length > 0 && (
          <StreamBlock
            title={items.length ? ru.assistantGeneralHints : ru.assistantHints}
            count={hints.general.length}
            open={hintsOpen}
            onToggle={() => setHintsOpen((v) => !v)}
          >
            <HintList hints={hints.general} items={state.items} />
          </StreamBlock>
        )}

        {state.transcript.length > 0 && (
          <StreamBlock
            title={ru.assistantHeard}
            count={state.transcript.length}
            open={transcriptOpen}
            onToggle={() => setTranscriptOpen((v) => !v)}
            showLabel={ru.assistantShowHeard}
            hideLabel={ru.assistantHideHeard}
          >
            <div className="agima-assistant-transcript">
              {state.transcript.map((line, index) => (
                <p key={`${line.at}-${index}`}>
                  {line.speaker ? <span className="agima-assistant-speaker">{line.speaker}: </span> : null}
                  {line.text}
                </p>
              ))}
            </div>
          </StreamBlock>
        )}

        {/* The empty agenda already explains what the bot is for. */}
        {meetingId && <BotFeed chat={chat} intro={items.length > 0} />}
      </div>

      {meetingId && <BotComposer chat={chat} />}
    </div>
  );
}
